import { merge } from 'lodash';
import ReactApexChart from 'react-apexcharts';

type ChartProps = {
  chartData: {
    name: string;
    data: number[];
  }[];
  chartLabels: string[];
  title: string;
  height?: number;
};

const Chart = ({ chartData, chartLabels, title, height = 400 }: ChartProps) => {
  const safeChartData = chartData.map(series => ({
    ...series,
    data: series.data.map(value => {
      if (typeof value === 'number' && isFinite(value)) {
        return Math.min(Math.max(value, -1e9), 1e9);
      }
      return 0;
    })
  }));

  const chartOptions = merge(BaseOptionChart(), {
    title: {
      text: title,
      align: 'left',
      style: {
        fontSize: '18px',
        fontWeight: 600,
      },
    },
    xaxis: {
      type: 'datetime',
      categories: chartLabels,
      labels: {
        formatter: function(value: string) {
          return new Date(value).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
        }
      },
      tickAmount: Math.min(chartLabels.length, 10),
      tickPlacement: 'on',
    },
    yaxis: {
      min: 0,
      max: Math.max(...safeChartData[0].data) * 1.1,
      labels: {
        formatter: (value: number) => {
          if (value >= 1e9) return (value / 1e9).toFixed(1) + 'B';
          if (value >= 1e6) return (value / 1e6).toFixed(1) + 'M';
          if (value >= 1e3) return (value / 1e3).toFixed(1) + 'K';
          return value.toFixed(0);
        }
      }
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (y: number) => {
          if (typeof y !== 'undefined') {
            return `${y.toFixed(0)}`;
          }
          return y;
        },
      },
    },
    stroke: {
      curve: 'smooth',
      width: 2,
    },
    markers: {
      size: 0,
    },
  });

  // Adjust colors based on the number of data series
  if (safeChartData.length === 1) {
    chartOptions.colors = ['#8B5CF6'];
  } else {
    chartOptions.colors = ['#8B5CF6', '#10B981'];
  }

  return (
    <ReactApexChart
      type='area'
      series={safeChartData}
      options={chartOptions as ApexCharts.ApexOptions}
      height={height}
    />
  );
};

export default Chart;

function BaseOptionChart() {
  return {
    chart: {
      toolbar: { show: false },
      zoom: { enabled: false },
      foreColor: 'gray',
    },
    colors: ['#8B5CF6'],
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.7,
        opacityTo: 0.1,
        stops: [0, 90, 100]
      },
    },
    dataLabels: { enabled: false },
    stroke: {
      width: 3,
      curve: 'smooth',
      lineCap: 'round',
    },
    grid: {
      strokeDashArray: 3,
      borderColor: 'rgba(0,0,0,0.1)',
    },
    xaxis: {
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        formatter: (value: number) => value.toFixed(0),
      },
    },
    tooltip: {
      x: {
        format: 'dd MMM yyyy',
      },
    },
    legend: {
      show: true,
      position: 'top',
      horizontalAlign: 'right',
    },
  };
}